import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

const impactBorder = (type) => {
  if (type === 'positive') return 'border-pine bg-pine-tint';
  if (type === 'negative') return 'border-hairline bg-bone';
  return 'border-hairline bg-bone-raised';
};

const VisionPortfolioInsights = ({ user, portfolioData }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.user_id && portfolioData) {
      fetchPortfolioVisionInsights();
    }
  }, [user?.user_id, portfolioData]);

  const fetchPortfolioVisionInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        '/api/vision-integration/analyze-portfolio-vision-impact',
        { user_id: user.user_id },
        { timeout: 60000 }
      );
      if (response.data.success) {
        setInsights(response.data.insights);
      } else {
        setError(response.data.error || 'Failed to load portfolio insights');
      }
    } catch (err) {
      setError(
        err.response?.status === 500
          ? 'Service temporarily unavailable. Please try again later.'
          : 'Failed to load portfolio insights'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="surface-panel p-6 flex items-center gap-3">
        <div className="w-6 h-6 border-2 border-hairline border-t-pine rounded-full animate-spin" />
        <span className="text-body text-ink-muted">Analyzing portfolio impact on your visions…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="surface-panel p-6 border-danger">
        <p className="text-body text-danger flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5" />
          {error}
        </p>
      </div>
    );
  }

  if (!insights?.vision_impacts?.length) {
    return (
      <div className="surface-panel p-6 text-center">
        <ChartBarIcon className="w-10 h-10 text-ink-faint mx-auto mb-3" />
        <h3 className="font-display text-h3 text-ink mb-1">No visions yet</h3>
        <p className="text-body text-ink-muted">
          Create a vision to see how your portfolio supports your goals.
        </p>
      </div>
    );
  }

  return (
    <motion.section
      className="surface-panel p-5 md:p-6"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6 pb-4 border-b border-hairline">
        <h3 className="font-display text-h2 text-ink">Portfolio impact on visions</h3>
        <div className="text-right">
          <p className="figure text-h2 text-pine">{Math.round(insights.total_impact_score * 100)}%</p>
          <p className="text-label text-ink-muted">Overall impact</p>
        </div>
      </div>

      {insights.overall_recommendation && (
        <p className="text-body text-ink-muted mb-6 pb-6 border-b border-hairline">
          {insights.overall_recommendation}
        </p>
      )}

      <ul className="space-y-4 hairline-list">
        {insights.vision_impacts.map((impact) => (
          <li key={impact.vision_id} className={`py-4 px-4 rounded-lg border ${impactBorder(impact.impact_type)}`}>
            <div className="flex justify-between gap-3 mb-2">
              <h4 className="font-display text-h3 text-ink">{impact.vision_title}</h4>
              <span className="figure text-body text-pine shrink-0">
                {Math.round(impact.impact_score * 100)}%
              </span>
            </div>
            <p className="text-body text-ink-muted mb-2">{impact.message}</p>
            {impact.recommended_actions?.length > 0 && (
              <ul className="text-label text-ink-muted space-y-1">
                {impact.recommended_actions.slice(0, 3).map((action, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-ink-faint">·</span>
                    {action}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </motion.section>
  );
};

export default VisionPortfolioInsights;
